import React, { useMemo } from "react";

import { getCategoryLabel } from "../../../intelligence-briefing-library/constants/kpiCategories";

export const CADENCE_ORDER = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annual",
  "manual",
];

const CADENCE_DROPDOWN_OPTIONS = [
  { value: "daily", label: "Real-time - daily" },
  { value: "weekly", label: "Bi-weekly - weekly" },
  { value: "monthly", label: "Monthly - monthly" },
  { value: "quarterly", label: "Quarterly - quarterly" },
  { value: "annual", label: "Annual - Annual" },
  { value: "manual", label: "Manual - manual" },
];

const CADENCE_DOT_COLOR_MAP: Record<string, string> = {
  daily: "bg-blue-500",
  weekly: "bg-sky-500",
  monthly: "bg-emerald-500",
  quarterly: "bg-amber-500",
  annual: "bg-indigo-400",
  manual: "bg-slate-400",
};

/** Same column geometry on every cadence table so STATUS / LAST INGESTED / CADENCE line up */
const CADENCE_TABLE_COL_WIDTHS = ["48%", "12%", "20%", "20%"] as const;

const cadenceTableThClass =
  "px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
const cadenceTableTdClass = "px-4 py-2.5 align-top";

export const toCadenceKey = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

export const normalizeCadenceOption = (value?: string) => {
  const key = toCadenceKey(value);
  switch (key) {
    case "daily":
    case "realtime":
      return "daily";
    case "weekly":
    case "biweekly":
      return "weekly";
    case "monthly":
      return "monthly";
    case "quarterly":
      return "quarterly";
    case "annual":
    case "yearly":
      return "annual";
    case "manual":
      return "manual";
    default:
      return "manual";
  }
};

export const toCadenceLabel = (cadence: string) => {
  const key = toCadenceKey(cadence);
  switch (key) {
    case "daily":
    case "realtime":
      return "Real-time";
    case "weekly":
    case "biweekly":
      return "Bi-weekly";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "annual":
      return "Annual";
    case "manual":
      return "Manual";
    default:
      return cadence || "Manual";
  }
};

interface OrgConfigCategoryMetric {
  name: string;
  heading?: string;
  cadence?: string;
  last_ingested?: string;
  metric_status?: string;
  coverage_detail?: { last_ingested?: string };
}

interface OrgConfigCategory {
  name: string;
  metrics: OrgConfigCategoryMetric[];
}

interface IngestionCadenceRow {
  key: string;
  categoryLabel: string;
  metricLabel: string;
  resolvedCadence: string;
  lastIngested: string;
  metricStatus: string;
}

interface IngestionCadenceSectionProps {
  orgConfigurationData?: {
    config?: {
      categories?: OrgConfigCategory[];
    };
  } | null;
  cadenceByMetricKey: Record<string, string>;
  onCadenceChange: (metricKey: string, cadence: string) => void;
  isSaving: boolean;
}

const toReadableLabel = (key: string) =>
  key
    .split("_")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getMetricHeading = (metric: OrgConfigCategoryMetric) => {
  const metricId = String(metric.name || "");
  if (typeof metric.heading === "string" && metric.heading.trim()) {
    return metric.heading.trim();
  }
  return toReadableLabel(metricId);
};

const resolveLastIngested = (metric: OrgConfigCategoryMetric) => {
  const direct =
    typeof metric?.last_ingested === "string"
      ? metric.last_ingested.trim()
      : "";
  if (direct) return direct;
  const nested =
    typeof metric?.coverage_detail?.last_ingested === "string"
      ? metric.coverage_detail.last_ingested.trim()
      : "";
  return nested;
};

export const formatMetricStatusLabel = (raw?: string) => {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (!s) return "";
  return s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const IngestionCadenceSection: React.FC<IngestionCadenceSectionProps> = ({
  orgConfigurationData,
  cadenceByMetricKey,
  onCadenceChange,
  isSaving,
}) => {
  const cadenceRowsByCadence = useMemo(() => {
    const categories =
      (
        orgConfigurationData?.config as {
          config_override?: { categories?: OrgConfigCategory[] };
          categories?: OrgConfigCategory[];
        } | null
      )?.config_override?.categories ||
      (
        orgConfigurationData as {
          config_override?: { categories?: OrgConfigCategory[] };
        } | null
      )?.config_override?.categories ||
      (orgConfigurationData?.config?.categories as OrgConfigCategory[]) ||
      (
        orgConfigurationData as {
          effective_config?: { categories?: OrgConfigCategory[] };
        } | null
      )?.effective_config?.categories ||
      [];
    const grouped: Record<string, IngestionCadenceRow[]> = {};
    CADENCE_ORDER.forEach(cadence => {
      grouped[cadence] = [];
    });

    categories.forEach(category => {
      const categoryId = String(category?.name || "");
      if (!categoryId) return;
      const categoryLabel =
        getCategoryLabel(categoryId) || toReadableLabel(categoryId);
      (Array.isArray(category?.metrics) ? category.metrics : []).forEach(
        metric => {
          const metricId = String(metric?.name || "");
          if (!metricId) return;
          const key = `${categoryId}::${metricId}`;
          const resolvedCadence =
            cadenceByMetricKey[key] || normalizeCadenceOption(metric?.cadence);
          const lastIngested = resolveLastIngested(metric);
          const metricStatusRaw =
            typeof metric?.metric_status === "string"
              ? metric.metric_status.trim()
              : "";
          const metricStatus = formatMetricStatusLabel(metricStatusRaw);
          if (!grouped[resolvedCadence]) grouped[resolvedCadence] = [];
          grouped[resolvedCadence].push({
            key,
            categoryLabel,
            metricLabel: getMetricHeading(metric),
            resolvedCadence,
            lastIngested,
            metricStatus,
          });
        }
      );
    });

    return grouped;
  }, [cadenceByMetricKey, orgConfigurationData]);

  const cadenceCounts = useMemo(
    () =>
      CADENCE_ORDER.map(cadence => ({
        cadence,
        count: (cadenceRowsByCadence[cadence] || []).length,
      })),
    [cadenceRowsByCadence]
  );

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200/70 bg-white shadow-sm">
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-4 overflow-x-auto border-b border-gray-100 pb-3">
          <div className="flex shrink-0 items-center gap-x-5">
            {cadenceCounts.map(({ cadence, count }) => (
              <div key={cadence} className="flex items-center gap-1.5 text-sm">
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                    CADENCE_DOT_COLOR_MAP[cadence] || "bg-slate-400"
                  }`}
                />
                <span className="font-semibold text-gray-700">
                  {toCadenceLabel(cadence)}
                </span>
                <span className="text-gray-500">({count})</span>
              </div>
            ))}
          </div>
          <p className="mb-0 shrink-0 border-l border-gray-200 pl-4 text-sm text-gray-600">
            Configure how often each metric refreshes from upstream sources.
          </p>
        </div>

        {CADENCE_ORDER.map(cadence => {
          const rows = cadenceRowsByCadence[cadence] || [];
          if (rows.length === 0) return null;
          return (
            <div
              key={cadence}
              className="w-full overflow-hidden rounded-lg border border-gray-100"
            >
              <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                    CADENCE_DOT_COLOR_MAP[cadence] || "bg-slate-400"
                  }`}
                />
                <span className="text-sm font-semibold text-gray-800">
                  {toCadenceLabel(cadence)}
                </span>
                <span className="text-xs text-gray-500">
                  {rows.length === 1 ? "1 metric" : `${rows.length} metrics`}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed border-collapse text-sm">
                  <colgroup>
                    {CADENCE_TABLE_COL_WIDTHS.map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <thead className="bg-white">
                    <tr className="border-b border-gray-100">
                      <th className={cadenceTableThClass}>Metric / Category</th>
                      <th className={cadenceTableThClass}>Status</th>
                      <th className={cadenceTableThClass}>Last Ingested</th>
                      <th className={cadenceTableThClass}>Cadence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr
                        key={row.key}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        <td className={`${cadenceTableTdClass} min-w-0`}>
                          <div className="break-words font-medium text-gray-800">
                            {row.metricLabel}
                          </div>
                          <div className="break-words text-xs text-gray-500">
                            {row.categoryLabel}
                          </div>
                        </td>
                        <td className={`${cadenceTableTdClass} text-gray-600`}>
                          {row.metricStatus || "-"}
                        </td>
                        <td className={`${cadenceTableTdClass} text-gray-600`}>
                          {row.lastIngested || "-"}
                        </td>
                        <td className={cadenceTableTdClass}>
                          <select
                            value={
                              cadenceByMetricKey[row.key] ?? row.resolvedCadence
                            }
                            onChange={event =>
                              onCadenceChange(row.key, event.target.value)
                            }
                            disabled={isSaving}
                            className="box-border w-full min-w-0 max-w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700"
                          >
                            {CADENCE_DROPDOWN_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IngestionCadenceSection;
